// ItemSearchPage.jsx
// Read-only item lookup: search, sort and page through the item master, and export the current
// search (all pages) to Excel.
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { Close, FileDownloadOutlined, ManageSearchOutlined, Search } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const formatAmount = (v) => (v === null || v === undefined || v === "" ? "" : Number(v).toFixed(2));
const formatRate = (v) => (v === null || v === undefined || v === "" ? "" : `${v}%`);

const ItemSearchPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortField, setSortField] = useState("itemName");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, page, rowsPerPage, sortOrder, sortField]);

  const fetchItems = async () => {
    const tenancyId = localStorage.getItem("tenancyId");
    const token = localStorage.getItem("jwtToken");

    setLoading(true);
    try {
      const res = await fetch(
        `/api/${tenancyId}/items-search?query=${encodeURIComponent(
          searchQuery
        )}&page=${page}&size=${rowsPerPage}&sortField=${sortField}&sortOrder=${sortOrder}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!res.ok) {
        console.error("Error: ", res.statusText);
        setItems([]);
        setTotalItems(0);
        return;
      }

      const data = await res.json();
      setItems(data.content || []);
      setTotalItems(data.totalElements || 0);
    } catch (err) {
      console.error("Error fetching items:", err);
      setItems([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleSort = (field) => {
    const isAsc = sortField === field && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortField(field);
    setPage(0);
  };

  const handleExportToExcel = async () => {
    const tenancyId = localStorage.getItem("tenancyId");
    const token = localStorage.getItem("jwtToken");

    setExporting(true);
    try {
      const res = await fetch(
        `/api/${tenancyId}/items-search?query=${encodeURIComponent(
          searchQuery
        )}&page=0&size=10000&sortField=${sortField}&sortOrder=${sortOrder}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        console.error("Failed to fetch items for Excel export");
        return;
      }
      const data = await res.json();
      const itemsToExport = data.content || [];

      const worksheetData = itemsToExport.map((item) => ({
        "Item Name": item.itemName,
        "Unit Name": item.unitName,
        "Unit ID": item.unitId,
        "Item Code": item.itemCode,
        "Item ID": item.itemId,
        "HSN Code": item.hsnCode,
        Barcode: item.barcode,
        "Standard Price": item.standardPrice,
        "Purchase Rate": item.purchaseRate,
        "Tax Rate": item.taxRate,
        "Cess Rate": item.cessRate,
      }));

      const ws = XLSX.utils.json_to_sheet(worksheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Items");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const fileData = new Blob([buf], { type: "application/octet-stream" });
      saveAs(fileData, "Item_List.xlsx");
    } catch (err) {
      console.error("Error exporting to Excel:", err);
    } finally {
      setExporting(false);
    }
  };

  const hideOnPhone = { display: { xs: "none", md: "table-cell" } };

  // [field, label, align, sortable, phone-visible, render]
  const columns = [
    ["itemName", "Item Name", "left", true, true, (i) => (
      <Typography variant="body2" sx={{ fontWeight: 500 }}>{i.itemName}</Typography>
    )],
    ["itemCode", "Item Code", "left", false, false, (i) => i.itemCode],
    ["barcode", "Barcode", "left", false, false, (i) => i.barcode],
    ["hsnCode", "HSN Code", "left", false, false, (i) => i.hsnCode],
    ["unitName", "Unit", "left", false, true, (i) => i.unitName],
    ["unitId", "Unit ID", "left", false, false, (i) => i.unitId],
    ["itemId", "Item ID", "left", false, false, (i) => i.itemId],
    ["standardPrice", "Standard Price", "right", true, true, (i) => formatAmount(i.standardPrice)],
    ["purchaseRate", "Purchase Rate", "right", false, false, (i) => formatAmount(i.purchaseRate)],
    ["taxRate", "Tax Rate", "right", false, false, (i) => formatRate(i.taxRate)],
    ["cessRate", "Cess Rate", "right", false, false, (i) => formatRate(i.cessRate)],
  ];

  return (
    <Box sx={{ width: "100%", maxWidth: 1400, mx: "auto", px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: { xs: 2, sm: 3 } }}>
        <ManageSearchOutlined color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            Item Search
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Look up items by name or code, and export the results to Excel.
          </Typography>
        </Box>
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={1.5}
          sx={{ p: 2 }}
        >
          <TextField
            size="small"
            autoFocus
            placeholder="Search by name or code"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ flexGrow: 1, maxWidth: { sm: 420 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchQuery("");
                      setPage(0);
                    }}
                    aria-label="Clear search"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Chip
            size="small"
            label={`${totalItems} ${totalItems === 1 ? "item" : "items"}`}
            sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
          />
          <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }} />
          <Button
            variant="outlined"
            onClick={handleExportToExcel}
            disabled={exporting || totalItems === 0}
            startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <FileDownloadOutlined />}
          >
            Export to Excel
          </Button>
        </Stack>
        <Box sx={{ height: 4 }}>{loading && <LinearProgress />}</Box>
        <TableContainer>
          <Table size="small" sx={{ "& th, & td": { px: { xs: 1, sm: 2 }, whiteSpace: { md: "nowrap" } } }}>
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover" } }}>
                {columns.map(([field, label, align, sortable, onPhone]) => (
                  <TableCell
                    key={field}
                    align={align}
                    sx={onPhone ? undefined : hideOnPhone}
                    sortDirection={sortField === field ? sortOrder : false}
                  >
                    {sortable ? (
                      <TableSortLabel
                        active={sortField === field}
                        direction={sortField === field ? sortOrder : "asc"}
                        onClick={() => handleSort(field)}
                      >
                        {label}
                      </TableSortLabel>
                    ) : (
                      label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {items.map((item, idx) => (
                <TableRow hover key={item.itemId || item.id || idx}>
                  {columns.map(([field, , align, , onPhone, render]) => (
                    <TableCell
                      key={field}
                      align={align}
                      sx={{
                        ...(onPhone ? {} : hideOnPhone),
                        ...(align === "right" ? { fontVariantNumeric: "tabular-nums" } : {}),
                      }}
                    >
                      {render(item)}
                      {field === "itemName" && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: { xs: "block", md: "none" } }}
                        >
                          {[item.itemCode, item.barcode].filter(Boolean).join(" · ")}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    {searchQuery ? `No items match "${searchQuery}".` : "No items found."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={(_e, np) => setPage(np)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default ItemSearchPage;
